import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Bell, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/settings/notifications")({
  head: () => ({ meta: [{ title: "Notificações — Viny Club" }] }),
  component: SettingsNotifications,
});

type Prefs = {
  messages: boolean;
  album_alerts: boolean;
  followers: boolean;
  email_new_follower: boolean;
  email_new_comment: boolean;
  email_wishlist_for_sale: boolean;
  email_new_message: boolean;
  email_streak_lost: boolean;
  email_inactivity: boolean;
  email_weekly_digest: boolean;
  email_ambassador: boolean;
  email_feature_spotlight: boolean;
  email_achievement: boolean;
  email_comeback: boolean;
};

const DEFAULTS: Prefs = {
  messages: true,
  album_alerts: true,
  followers: true,
  email_new_follower: false,
  email_new_comment: false,
  email_wishlist_for_sale: true,
  email_new_message: true,
  email_streak_lost: true,
  email_inactivity: true,
  email_weekly_digest: true,
  email_ambassador: true,
  email_feature_spotlight: true,
  email_achievement: true,
  email_comeback: true,
};

const IN_APP_ROWS: Array<{ key: keyof Prefs; label: string; desc: string }> = [
  { key: "messages", label: "Mensagens", desc: "Avisos quando você receber mensagens no chat." },
  { key: "album_alerts", label: "Alertas de discos", desc: "Avisos quando álbuns da sua wishlist entrarem à venda." },
  { key: "followers", label: "Seguidores", desc: "Avisos quando alguém começar a te seguir." },
];

const EMAIL_ROWS: Array<{ key: keyof Prefs; label: string; desc: string }> = [
  { key: "email_new_comment", label: "Comentário no seu post", desc: "Email quando alguém comenta no seu post (feed, grupo ou álbum)." },
  { key: "email_new_message", label: "Nova mensagem", desc: "Email quando você recebe uma mensagem no chat." },
  { key: "email_wishlist_for_sale", label: "Wishlist à venda", desc: "Email quando um álbum da sua wishlist entra à venda." },
  { key: "email_streak_lost", label: "Ofensiva perdida", desc: "Aviso quando você quebra sua sequência de dias ativos." },
];

const LIFECYCLE_ROWS: Array<{ key: keyof Prefs; label: string; desc: string }> = [
  { key: "email_weekly_digest", label: "Resumo semanal", desc: "Toda segunda, um compilado com novos seguidores, engajamento, XP e recomendações de garimpo." },
  { key: "email_ambassador", label: "Embaixador", desc: "Convites e avisos sobre limite da coleção e upgrade pro plano Embaixador." },
  { key: "email_feature_spotlight", label: "Novidades e recursos", desc: "Série curta de emails apresentando garimpo, grupos e mini-site." },
  { key: "email_achievement", label: "Conquistas desbloqueadas", desc: "Aviso quando você desbloqueia uma nova conquista. No máximo 1 por dia." },
  { key: "email_comeback", label: "Sentimos sua falta", desc: "Um empurrãozinho após 14 dias sem entrar, com o que rolou por aqui." },
  { key: "email_new_follower", label: "Novo seguidor (individual)", desc: "Email por seguidor novo. Desligado por padrão — o resumo semanal já traz todos juntos." },
  { key: "email_inactivity", label: "Reengajamento antigo", desc: "Emails legados de 7 e 30 dias sem interagir. No máximo 1 por semana." },
];


function SettingsNotifications() {
  const { user, isAuthenticated } = useCurrentUser();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<keyof Prefs | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) toast.error(error.message);
      if (data) setPrefs({ ...DEFAULTS, ...data });
      setLoading(false);
    })();
  }, [user?.id]);

  if (!isAuthenticated) {
    return <p className="text-muted-foreground">Entre para gerenciar suas notificações.</p>;
  }

  async function update(key: keyof Prefs, value: boolean) {
    if (!user?.id) return;
    const previous = prefs;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(key);
    const { error } = await supabase
      .from("notification_preferences")
      .upsert({ user_id: user.id, ...next }, { onConflict: "user_id" });
    setSaving(null);
    if (error) {
      setPrefs(previous);
      toast.error(error.message);
    }
  }

  return (
    <div className="space-y-3">
      <section className="rounded-2xl border border-border bg-surface p-5">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Notificações no app</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          O que aparece na sininha e nos avisos dentro da plataforma.
        </p>

        <div className="mt-4 divide-y divide-border">
          {IN_APP_ROWS.map((row) => (
            <Row
              key={row.key}
              row={row}
              value={prefs[row.key]}
              disabled={loading}
              saving={saving === row.key}
              onChange={(v) => update(row.key, v)}
            />
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Emails</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          O que a gente pode te mandar por email. Você pode desativar tudo a qualquer momento —
          e cada email tem um link de descadastro instantâneo.
        </p>

        <div className="mt-4 divide-y divide-border">
          {EMAIL_ROWS.map((row) => (
            <Row
              key={row.key}
              row={row}
              value={prefs[row.key]}
              disabled={loading}
              saving={saving === row.key}
              onChange={(v) => update(row.key, v)}
            />
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Resumos, Embaixador e novidades</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Emails periódicos sobre a sua jornada na Viny. Tudo com throttle — nunca mais de um por dia
          por tema, e o resumo semanal agrupa novos seguidores para não lotar sua caixa.
        </p>

        <div className="mt-4 divide-y divide-border">
          {LIFECYCLE_ROWS.map((row) => (
            <Row
              key={row.key}
              row={row}
              value={prefs[row.key]}
              disabled={loading}
              saving={saving === row.key}
              onChange={(v) => update(row.key, v)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}


function Row({
  row,
  value,
  disabled,
  saving,
  onChange,
}: {
  row: { label: string; desc: string };
  value: boolean;
  disabled: boolean;
  saving: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div>
        <p className="text-sm font-semibold">{row.label}</p>
        <p className="text-xs text-muted-foreground">{row.desc}</p>
      </div>
      <div className="flex items-center gap-2">
        {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <Switch checked={value} disabled={disabled} onCheckedChange={onChange} />
      </div>
    </div>
  );
}
